import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { AddShareholderSearchComponent } from '../add-shareholder-search/add-shareholder-search.component';
import { LargeShareholderFormComponent } from '../../../../../large-shareholders/large-shareholder-form/large-shareholder-form.component';
import { LargeShareholderService } from '../../../../../../services/large-shareholder.service';
import { CreateLargeShareholderRequest } from '../../../../../../models/api/createLargeShareholderRequest';
import { GlobalSearchResultDto } from '../../../../../../models/api/globalSearchResultDto';
import { EntityType } from '../../../../../../models/enums/entityType.enum';
import { InfoDialogService } from '../../../../../../services/info-dialog.service';
import { Helpers } from '../../../../../../utilities/helpers';

export interface AddShareholderDialogResult {
  action: 'connect' | 'created';
  item: GlobalSearchResultDto & { categoryName?: string; subCategoryName?: string };
}

type DialogMode = 'add-new' | 'search-connect' | 'edit';

@Component({
  selector: 'app-add-shareholder-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    NgxSpinnerModule,
    AddShareholderSearchComponent,
    LargeShareholderFormComponent
  ],
  templateUrl: './add-shareholder-dialog.component.html',
  styleUrls: ['./add-shareholder-dialog.component.scss']
})
export class AddShareholderDialogComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private largeShareholderService = inject(LargeShareholderService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private infoDialogService = inject(InfoDialogService);
  private data = inject<{ year: string }>(MAT_DIALOG_DATA, { optional: true });

  @ViewChild(LargeShareholderFormComponent) formComponent?: LargeShareholderFormComponent;

  currentMode: DialogMode = 'search-connect';
  selectedItem: GlobalSearchResultDto | null = null;

  // Edit mode state
  editShareholderId: string | null = null;
  editShareholderName: string | null = null;

  // Form for creating a new large shareholder
  createForm: FormGroup = this.fb.group({
    year: [this.data?.year || Helpers.defaultMeasurementYear(), Validators.required],
    fullName: ['', Validators.required],
    address: [''],
    website: [''],
    email: [''],
    country: [''],
    category: [''],
    subcategory: [''],
    notes: ['']
  });

  // Form for editing an existing large shareholder
  editForm: FormGroup = this.fb.group({
    year: [this.data?.year || Helpers.defaultMeasurementYear(), Validators.required],
    fullName: ['', Validators.required],
    address: [''],
    website: [''],
    email: [''],
    country: [''],
    category: [''],
    subcategory: [''],
    notes: ['']
  });

  constructor(
    public dialogRef: MatDialogRef<AddShareholderDialogComponent>
  ) {}

  ngOnDestroy(): void {
    this.cleanupEditState();
  }

  getDialogTitle(): string {
    if (this.currentMode === 'add-new') {
      return 'Create New Large Shareholder';
    }
    if (this.currentMode === 'edit') {
      return `Edit Large Shareholder — ${this.editShareholderName || ''}`;
    }
    return 'Add Large Shareholder';
  }

  onBack(): void {
    this.currentMode = 'search-connect';
    this.selectedItem = null;
    this.cleanupEditState();
  }

  onAddNewFromSearch(): void {
    this.currentMode = 'add-new';
  }

  onEditShareholder(item: GlobalSearchResultDto): void {
    if (item.entityType !== EntityType.LargeShareholder && item.entityType !== EntityType.PyramidalShareholder) {
      return;
    }
    this.editShareholderId = item.id;
    this.editShareholderName = item.name;
    this.currentMode = 'edit';
    this.loadShareholderForEdit(item.id);
  }

  onShareholderSelected(item: GlobalSearchResultDto | null): void {
    this.selectedItem = item;
  }

  onCreateSubmit(): void {
    if (!this.createForm.valid) return;

    const formValue = this.createForm.value;
    const request: CreateLargeShareholderRequest = {
      year: parseInt(formValue.year),
      name: formValue.fullName,
      address: formValue.address || '',
      website: formValue.website || '',
      email: formValue.email || '',
      countryId: formValue.country?.id || formValue.country || '',
      categoryId: formValue.category?.id || formValue.category || '',
      subCategoryId: formValue.subcategory?.id || formValue.subcategory || '',
      notes: formValue.notes || ''
    };

    this.spinnerService.show();
    this.largeShareholderService.createLargeShareholder(request).subscribe({
      next: (created) => {
        this.spinnerService.hide();
        this.toast.success('Large shareholder created successfully');
        const result: AddShareholderDialogResult = {
          action: 'created',
          item: {
            id: created.id,
            name: created.name,
            entityType: EntityType.LargeShareholder,
            categoryName: created.categoryName,
            subCategoryName: created.subCategoryName
          }
        };
        this.dialogRef.close(result);
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toast.error('Error creating large shareholder');
        console.error('Error creating large shareholder:', error);
      }
    });
  }

  onEditSubmit(): void {
    if (!this.editForm.valid || !this.editShareholderId) return;

    const formValue = this.editForm.value;
    const request = {
      year: parseInt(formValue.year),
      name: formValue.fullName,
      address: formValue.address || '',
      website: formValue.website || '',
      email: formValue.email || '',
      countryId: formValue.country?.id || formValue.country || '',
      categoryId: formValue.category?.id || formValue.category || '',
      subCategoryId: formValue.subcategory?.id || formValue.subcategory || '',
      notes: formValue.notes || ''
    };

    this.spinnerService.show();
    this.largeShareholderService.updateLargeShareholder(this.editShareholderId, request).subscribe({
      next: (updated) => {
        this.spinnerService.hide();
        this.toast.success('Large shareholder updated successfully');
        this.editShareholderName = updated.name;
        this.editForm.markAsPristine();
      },
      error: (error) => {
        console.error('Error updating large shareholder:', error);
        this.spinnerService.hide();
        this.infoDialogService.openErrorDialog(
          'Failed to update large shareholder. Please try again.',
          'Update Error'
        );
      }
    });
  }

  onConfirmConnect(): void {
    if (!this.selectedItem) return;
    const result: AddShareholderDialogResult = {
      action: 'connect',
      item: this.selectedItem
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private loadShareholderForEdit(id: string): void {
    this.spinnerService.show();
    this.largeShareholderService.getLargeShareholderById(id).subscribe({
      next: (shareholder) => {
        if (shareholder) {
          const formValue = {
            year: shareholder.year?.toString() || this.data?.year || Helpers.defaultMeasurementYear(),
            fullName: shareholder.name,
            address: shareholder.address,
            website: shareholder.website,
            email: shareholder.email,
            country: shareholder.countryId ? { id: shareholder.countryId, name: shareholder.countryName } : null,
            category: shareholder.categoryId ? { id: shareholder.categoryId, name: shareholder.categoryName } : null,
            subcategory: shareholder.subCategoryId ? { id: shareholder.subCategoryId, name: shareholder.subCategoryName } : null,
            notes: shareholder.notes
          };

          this.editShareholderName = shareholder.name;
          this.editForm.patchValue(formValue, { emitEvent: false });

          // Trigger category change to load subcategories
          if (shareholder.categoryId) {
            this.editForm.get('category')?.setValue(
              { id: shareholder.categoryId, name: shareholder.categoryName },
              { emitEvent: true }
            );
            // Re-set subcategory after category change triggers subcategory loading
            setTimeout(() => {
              if (shareholder.subCategoryId) {
                this.editForm.get('subcategory')?.setValue(
                  { id: shareholder.subCategoryId, name: shareholder.subCategoryName }
                );
              }
            }, 0);
          }
        }
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading large shareholder:', error);
        this.spinnerService.hide();
        this.toast.error('Error loading large shareholder');
        this.currentMode = 'search-connect';
      }
    });
  }

  private cleanupEditState(): void {
    this.editShareholderId = null;
    this.editShareholderName = null;
    this.editForm.reset({
      year: this.data?.year || Helpers.defaultMeasurementYear(),
      fullName: '',
      address: '',
      website: '',
      email: '',
      country: '',
      category: '',
      subcategory: '',
      notes: ''
    });
  }
}
